import { z } from 'zod';
import { lenientDateString, lenientNumber } from '../schema-extensions';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const ODOMETER_READING_CONSTANTS = {
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  MIN_READING: 0,
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for odometer reading - accepts various formats and coerces to standard
 */
const lenientOdometerReadingNodeSchema = z.object({
  date: lenientDateString(''),
  reading: lenientNumber(0, ODOMETER_READING_CONSTANTS.MIN_READING),
}).transform(val => ({
  date: val.date,
  reading: val.reading
}));

/**
 * Strict schema for odometer reading - enforces exact format
 */
const strictOdometerReadingNodeSchema = z.object({
  date: z.string().length(ODOMETER_READING_CONSTANTS.DATE_FORMAT_LENGTH),
  reading: z.number().min(ODOMETER_READING_CONSTANTS.MIN_READING),
}).transform(val => ({
  date: val.date,
  reading: val.reading
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface OdometerReadingNode {
  date: string;
  reading: number;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace OdometerReadingNode {
  /**
   * Safely deserialize unknown data into OdometerReadingNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): OdometerReadingNode {
    return lenientOdometerReadingNodeSchema.parse(data);
  }

  /**
   * Create a new OdometerReadingNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: OdometerReadingNode): OdometerReadingNode {
    return strictOdometerReadingNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to OdometerReadingNode structure
   */
  export function validate(data: unknown): data is OdometerReadingNode {
    return strictOdometerReadingNodeSchema.safeParse(data).success;
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = ODOMETER_READING_CONSTANTS;
}
